import { useCallback, useEffect, useRef } from 'react';

import {
  LEFT_PANEL_MAX_WIDTH,
  LEFT_PANEL_MIN_WIDTH,
  clampLeftPanelWidth,
} from '../state/ui-store.js';

export interface UseLeftPanelResizeArgs {
  readonly currentWidth: number;
  readonly setWidth: (width: number) => void;
}

export interface UseLeftPanelResizeReturn {
  readonly onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  readonly resizing: React.RefObject<boolean>;
}

export function useLeftPanelResize({
  currentWidth,
  setWidth,
}: UseLeftPanelResizeArgs): UseLeftPanelResizeReturn {
  const resizing = useRef(false);
  const dragStartXRef = useRef(0);
  const startWidthRef = useRef(currentWidth);

  const handleMove = useCallback(
    (event: PointerEvent) => {
      if (!resizing.current) return;
      const dx = event.clientX - dragStartXRef.current;
      const next = clampLeftPanelWidth(startWidthRef.current + dx);
      setWidth(next);
    },
    [setWidth],
  );

  const handleUp = useCallback(() => {
    if (!resizing.current) return;
    resizing.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('pointermove', handleMove);
    window.removeEventListener('pointerup', handleUp);
    window.removeEventListener('pointercancel', handleUp);
  }, [handleMove]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      resizing.current = true;
      dragStartXRef.current = event.clientX;
      startWidthRef.current = currentWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
    },
    [currentWidth, handleMove, handleUp],
  );

  useEffect(() => {
    return (): void => {
      if (resizing.current) {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [handleMove, handleUp]);

  return { onPointerDown, resizing };
}

export const LEFT_PANEL_RESIZE_BOUNDS = {
  min: LEFT_PANEL_MIN_WIDTH,
  max: LEFT_PANEL_MAX_WIDTH,
} as const;
